import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../components/apiconfig/api-config';
import { FaArrowLeft } from 'react-icons/fa';

const SanForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    commodity_id: '',
    vessel_name: '',
    warehouse_id: '',
    adjustment_type: 'Excess',
    quantity_mt: '',
    remarks: ''
  });
  
  const [commodities, setCommodities] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCommodities = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/masters/dropdown-commodities`, { withCredentials: true });
        setCommodities(res.data.data || []);
      } catch (err) {
        console.error('Error fetching commodities:', err);
      }
    };
    fetchCommodities();
  }, []);

  useEffect(() => {
    const fetchWarehouses = async () => {
      if (!formData.commodity_id) {
        setWarehouses([]);
        return;
      }
      try {
        const res = await axios.get(`${API_BASE_URL}/api/masters/dropdown-warehouses-by-commodity?commodity_id=${formData.commodity_id}`, { withCredentials: true });
        setWarehouses(res.data.data || []);
      } catch (err) {
        console.error('Error fetching warehouses:', err);
      }
    };
    fetchWarehouses();
  }, [formData.commodity_id]);

  useEffect(() => {
    const fetchVessels = async () => {
      if (!formData.commodity_id || !formData.warehouse_id) {
        setVessels([]);
        return;
      }
      try {
        const res = await axios.get(`${API_BASE_URL}/api/masters/dropdown-vessels-by-commodity-and-warehouse?commodity_id=${formData.commodity_id}&warehouse_id=${formData.warehouse_id}`, { withCredentials: true });
        setVessels(res.data.data || []);
      } catch (err) {
        console.error('Error fetching vessels:', err);
      }
    };
    fetchVessels();
  }, [formData.commodity_id, formData.warehouse_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      if (name === 'commodity_id') {
        updated.warehouse_id = '';
        updated.vessel_name = '';
      } else if (name === 'warehouse_id') {
        updated.vessel_name = '';
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.commodity_id || !formData.warehouse_id || !formData.quantity_mt) {
      setError('Please fill all required fields');
      return;
    }
    
    if (parseFloat(formData.quantity_mt) <= 0) {
      setError('Quantity must be greater than 0');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_BASE_URL}/api/stock-adjustment/san-post`, formData, { withCredentials: true });
      navigate('/san');
    } catch (err) {
      console.error('Error creating SAN:', err);
      setError(err.response?.data?.error || 'Failed to create Stock Adjustment Note');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <h2 className="text-lg font-bold mb-0 text-left">Create Stock Adjustment Note (SAN)</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-2 py-0.5 rounded text-xs font-medium hover:bg-[#eaf6fd]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-4 pt-4 pb-2">
          {error && (
            <div className="p-3 mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Commodity */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Commodity <span className="text-red-500">*</span>
              </label>
              <select
                name="commodity_id"
                value={formData.commodity_id}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="">Select Commodity</option>
                {commodities.map(c => (
                  <option key={c.commodity_id} value={c.commodity_id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Warehouse */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Warehouse / Port Name <span className="text-red-500">*</span>
              </label>
              <select
                name="warehouse_id"
                value={formData.warehouse_id}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="">Select Warehouse / Port</option>
                {warehouses.map(w => (
                  <option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>
                ))}
              </select>
            </div>

            {/* Vessel */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Vessel Name
              </label>
              <select
                name="vessel_name"
                value={formData.vessel_name}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="">Select Vessel Name</option>
                {vessels.map((v, idx) => (
                  <option key={idx} value={v.vessel_name}>{v.vessel_name}</option>
                ))}
              </select>
            </div>

            {/* Adjustment Type */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Type of Stock Adjustment <span className="text-red-500">*</span>
              </label>
              <select
                name="adjustment_type"
                value={formData.adjustment_type}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="Excess">Excess (+)</option>
                <option value="Shortage">Shortage (-)</option>
              </select>
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                SAN Quantity (MT) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="quantity_mt"
                value={formData.quantity_mt}
                onChange={handleChange}
                step="0.001"
                min="0.001"
                required
                placeholder="Enter Quantity"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* Remarks */}
            <div className="lg:col-span-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Remarks / Reason
              </label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                rows="1"
                placeholder="Enter reason for adjustment..."
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              ></textarea>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={() => navigate('/san')}
              className="px-3 py-1 text-xs border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-3 py-1 text-xs border border-transparent rounded text-white bg-[#3899cf] hover:bg-[#0e8dc6] font-medium disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Submit Note'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SanForm;
